<template>
  <template v-if="$route.name === 'payment-receipts.index'">
    <div class="col-12 col-xl-12">
      <div class="container-fluid px-0">
        <div class="row mb-4 g-3 align-items-end">
          <div class="col-6 col-md-3 col-lg-2">
            <select
              v-model="academicYearFilter"
              class="form-select form-select-sm"
              @change="fetchReceipts"
            >
              <option value="">{{ t("receipts.all_academic_years") }}</option>
              <option
                v-for="year in academicYears"
                :value="year.id"
                :key="year.id"
              >
                {{ year.name }}
              </option>
            </select>
          </div>
          <div class="col-6 col-md-2 col-lg-2">
            <select
              v-model="guardianFilter"
              class="form-select form-select-sm"
              @change="fetchReceipts"
            >
              <option value="">{{ t("receipts.all_guardians") }}</option>
              <option
                v-for="guardian in guardians"
                :value="guardian.id"
                :key="guardian.id"
              >
                {{ guardian.username }}
              </option>
            </select>
          </div>
          <div class="col-12 col-md-3 col-lg-3">
            <div class="input-group input-group-sm">
              <span class="input-group-text bg-white border-end-0">
                <i class="fas fa-search text-muted"></i>
              </span>
              <input
                type="text"
                class="form-control border-start-0"
                :placeholder="t('receipts.search_by_number')"
                v-model="receiptNumberFilter"
                @input="debouncedSearch"
              />
            </div>
          </div>
          <div class="col-6 col-md-1 d-none d-md-block">
            <button
              class="btn btn-outline-secondary btn-sm w-100"
              @click="resetFilters"
              :title="t('receipts.reset_filter')"
            >
              <i class="fas fa-filter-circle-xmark"></i>
            </button>
          </div>
          <div class="col-6 d-md-none">
            <button
              class="btn btn-outline-secondary btn-sm w-100"
              @click="resetFilters"
            >
              <i class="fas fa-filter-circle-xmark me-1"></i>
              {{ t("receipts.reset") }}
            </button>
          </div>
          <div class="col-6 col-md-4 col-lg-4">
            <router-link
              :to="{ name: 'payment-receipts.create' }"
              class="btn btn-secondary btn-sm w-100"
              v-can="'payment_receipts.create'"
            >
              <i class="fas fa-plus me-1"></i>
              {{ t("receipts.add_receipt") }}
            </router-link>
          </div>
          <div class="col-6 col-md-4 col-lg-2">
            <router-link
              :to="{ name: 'payment-receipts.discount' }"
              class="btn btn-outline-secondary btn-sm w-100"
              v-can="'payment_receipts.create'"
            >
              <i class="fas fa-tag me-1"></i>
              {{ t("receipts.discount_button") }}
            </router-link>
          </div>
          <div class="col-12 col-md-3 col-lg-3">
            <button
              class="btn btn-primary btn-sm w-100 print-hide"
              @click="printReceiptsReport"
            >
              <i class="fas fa-print me-1"></i>
              {{ t("receipts.print_report") }}
            </button>
          </div>
        </div>
      </div>

      <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
        <div
          class="card-header p-0 position-relative mt-n4 mx-3 z-index-2"
          style="height: 60px"
        >
          <div
            class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3"
          >
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="text-white text-capitalize ps-3">
                {{ t("receipts.receipt_record") }}
              </h6>
            </div>
          </div>
        </div>

        <div class="card-body px-0 pb-2">
          <LoadingSpinner
            v-if="isLoading"
            :message="t('receipts.loading_receipts')"
          />
          <template v-else>
            <div class="d-none d-xl-block">
              <div class="table-responsive">
                <table class="table align-items-center mb-0">
                  <thead>
                    <tr>
                      <th class="text-center">{{ t("receipts.number") }}</th>
                      <th class="text-center">{{ t("receipts.date") }}</th>
                      <th class="text-center">{{ t("receipts.notes") }}</th>
                      <th class="text-center">{{ t("receipts.student") }}</th>
                      <th class="text-center">{{ t("receipts.academic_year") }}</th>
                      <th class="text-center">{{ t("receipts.discount_column") }}</th>
                      <th class="text-center">{{ t("receipts.credit") }}</th>
                      <th class="text-center">{{ t("receipts.debit") }}</th>
                      <th class="text-center">{{ t("receipts.balance") }}</th>
                      <th class="text-center">{{ t("receipts.actions") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="receipt in receiptsWithYearBalance"
                      :key="receipt.id"
                      :class="{
                        'text-muted': receipt.deleted_at || receipt.is_reversal,
                        'text-danger': receipt.is_reversal,
                      }"
                    >
                      <td class="text-center">
                        {{ receipt.receipt_number }}
                        <span
                          v-if="receipt.deleted_at"
                          class="badge bg-danger ms-2"
                          >{{ t("receipts.deleted") }}</span
                        >
                        <span
                          v-if="receipt.is_reversal"
                          class="badge bg-warning ms-2"
                          >{{ t("receipts.reversal") }}</span
                        >
                      </td>
                      <td class="text-center text-nowrap">{{ formatDate(receipt.created_at) }}</td>
                      <td class="text-center">
                        {{ receipt.notes }}
                      </td>
                      <td class="text-center">{{ receipt.student_name }}</td>
                      <td class="text-center">{{ receipt.academic_year?.name }}</td>
                      <td class="text-center">
                        <span
                          v-if="receipt.discount_type && receipt.discount_type !== 'none' && receipt.discount_amount > 0"
                          class="badge bg-info-subtle text-dark"
                          :title="t('receipts.discount_applied_hint', { percentage: receipt.discount_percentage })"
                        >
                          <i class="fas fa-tag me-1"></i>
                          {{ t("receipts.discount_badge", { percentage: receipt.discount_percentage, amount: formatCurrency(receipt.discount_amount) }) }}
                        </span>
                        <span v-else class="text-muted">—</span>
                      </td>
                      <td class="text-center text-danger">
                        <span v-if="receipt.credit > 0">{{
                          formatCurrency(receipt.credit)
                        }}</span>
                        <span v-else>{{ formatCurrency(0) }}</span>
                      </td>
                      <td class="text-center text-success">
                        <span v-if="paidAmount(receipt) > 0">{{
                          formatCurrency(paidAmount(receipt))
                        }}</span>
                        <span v-else>{{ formatCurrency(0) }}</span>
                      </td>
                      <td class="text-center">
                        <span>{{
                          formatCurrency(receipt.year_running_balance)
                        }}</span>
                      </td>
                      <td class="text-center">
                        <div class="d-flex gap-1 justify-content-center">
                          <button
                            @click="printSingleReceipt(receipt)"
                            class="btn btn-sm text-primary"
                            :disabled="
                              receipt.id === null || receipt.is_reversal
                            "
                          >
                            <i class="fas fa-print"></i>
                          </button>

                          <router-link
                            v-if="
                              !receipt.deleted_at &&
                              receipt.id !== null &&
                              !receipt.is_reversal
                            "
                            :to="{
                              name: 'payment-receipts.edit',
                              params: { id: receipt.id },
                            }"
                            class="btn btn-sm text-secondary"
                            v-can="'payment_receipts.update'"
                          >
                            <i class="fas fa-edit"></i>
                          </router-link>
                          <button
                            v-else
                            class="btn btn-sm text-secondary"
                            disabled
                          >
                            <i class="fas fa-edit"></i>
                          </button>

                          <button
                            class="btn btn-sm"
                            :class="
                              receipt.deleted_at || receipt.is_reversal
                                ? 'btn-secondary disabled'
                                : 'text-danger'
                            "
                            @click="openDeleteDialog(receipt)"
                            :disabled="
                              !!receipt.deleted_at ||
                              receipt.id === null ||
                              receipt.is_reversal
                            "
                            v-can="'payment_receipts.delete'"
                          >
                            <i class="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="d-block d-xl-none">
              <div
                v-if="receiptsWithYearBalance.length === 0"
                class="text-center py-3 text-muted"
              >
                <i class="fas fa-receipt fa-2x mb-2"></i>
                <p>{{ t("receipts.empty") }}</p>
              </div>
              <div
                v-for="receipt in receiptsWithYearBalance"
                :key="'mobile-' + receipt.id"
                class="card mb-3 mx-2"
                :class="{
                  'border-warning': receipt.is_reversal,
                  'opacity-75': receipt.deleted_at,
                }"
              >
                <div class="card-body py-2">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 class="mb-0 text-sm">
                        {{ t("receipts.number") }}: {{ receipt.receipt_number }}
                        <span
                          v-if="receipt.deleted_at"
                          class="badge bg-danger ms-2"
                          >{{ t("receipts.deleted") }}</span
                        >
                        <span
                          v-if="receipt.is_reversal"
                          class="badge bg-warning ms-2"
                          >{{ t("receipts.reversal") }}</span
                        >
                      </h6>
                      <p class="text-xs text-muted mb-1">
                        {{ t("receipts.date") }}: {{ formatDate(receipt.created_at) }}
                      </p>
                      <p class="text-xs text-muted mb-1">
                        {{ t("receipts.student") }}: {{ receipt.student_name }}
                      </p>
                      <p class="text-xs text-muted mb-1">
                        {{ t("receipts.academic_year") }}: {{ receipt.academic_year?.name }}
                      </p>
                      <p class="text-xs text-muted mb-1">
                        {{ t("receipts.notes") }}: {{ receipt.notes }}
                      </p>
                    </div>
                    <div class="d-flex">
                      <button
                        @click="printSingleReceipt(receipt)"
                        class="btn btn-link text-primary p-0 mx-1"
                        :title="t('receipts.print_report')"
                        :disabled="receipt.id === null || receipt.is_reversal"
                      >
                        <i class="fas fa-print fa-xs"></i>
                      </button>
                      <router-link
                        v-if="
                          !receipt.deleted_at &&
                          receipt.id !== null &&
                          !receipt.is_reversal
                        "
                        :to="{
                          name: 'payment-receipts.edit',
                          params: { id: receipt.id },
                        }"
                        class="btn btn-link text-secondary p-0 mx-1"
                        :title="t('receipts.edit')"
                        v-can="'payment_receipts.update'"
                      >
                        <i class="fas fa-edit fa-xs"></i>
                      </router-link>
                      <button
                        v-else
                        class="btn btn-link text-secondary p-0 mx-1"
                        disabled
                        :title="t('receipts.edit')"
                      >
                        <i class="fas fa-edit fa-xs"></i>
                      </button>
                      <button
                        @click="openDeleteDialog(receipt)"
                        class="btn btn-link text-danger p-0 mx-1"
                        :title="t('receipts.delete')"
                        v-can="'payment_receipts.delete'"
                        :disabled="
                          !!receipt.deleted_at ||
                          receipt.id === null ||
                          receipt.is_reversal
                        "
                      >
                        <i class="fas fa-trash fa-xs"></i>
                      </button>
                    </div>
                  </div>

                  <div class="row mt-2">
                    <div
                      class="col-6 small"
                      v-if="receipt.discount_type && receipt.discount_type !== 'none' && receipt.discount_amount > 0"
                    >
                      <span class="fw-bold">{{ t("receipts.discount_column") }}:</span>
                      <span class="badge bg-info-subtle text-dark">
                        <i class="fas fa-tag me-1"></i>
                        {{ t("receipts.discount_badge", { percentage: receipt.discount_percentage, amount: formatCurrency(receipt.discount_amount) }) }}
                      </span>
                    </div>
                    <div class="col-6 small">
                      <span class="fw-bold">{{ t("receipts.credit") }}:</span>
                      <span class="text-danger" v-if="receipt.credit > 0"
                        > {{ formatCurrency(receipt.credit) }}</span
                      >
                      <span v-else>{{ formatCurrency(0) }}</span>
                    </div>
                    <div class="col-6 small mt-2">
                      <span class="fw-bold">{{ t("receipts.debit") }}:</span>
                      <span class="text-success" v-if="paidAmount(receipt) > 0">{{
                        formatCurrency(paidAmount(receipt))
                      }}</span>
                      <span v-else>{{ formatCurrency(0) }}</span>
                    </div>
                    <div class="col-6 small mt-2">
                      <span class="fw-bold">{{ t("receipts.balance") }}:</span>
                      <span>{{
                          formatCurrency(receipt.year_running_balance)
                        }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <PaginationControls
              v-if="!isLoading && pagination.total > pagination.perPage"
              :pagination="pagination"
              @page-changed="handlePageChange"
              @per-page-changed="handlePerPageChange"
            />
          </template>
        </div>
      </div>
    </div>

    <SoftDelete
      ref="deleteDialog"
      :receipt-number="selectedReceipt?.receipt_number || ''"
      @confirm="deleteReceipt"
    />
  </template>
  <router-view></router-view>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";
import api from "@/api";
import PaginationControls from "@/components/common/PaginationControls.vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import SoftDelete from "@/components/common/softDelete.vue";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();
const store = useStore();
const toast = useToast();

const receipts = ref([]);
const guardians = ref([]);
const academicYearFilter = ref("");
const academicYears = ref([]);
const receiptNumberFilter = ref("");
const guardianFilter = ref("");
const isLoading = ref(false);
let debounceTimeout = null;
const deleteDialog = ref(null);
const selectedReceipt = ref(null);

const loadAcademicYears = async () => {
  try {
    const response = await api.get("/academic-years");
    academicYears.value = response.data.data || [];

    const currentYear = academicYears.value.find((y) => y.is_active);
    if (currentYear) {
      academicYearFilter.value = currentYear.id;
    }
  } catch (error) {
    console.error("Error loading academic years:", error);
  }
};

const pagination = computed(
  () =>
    store.getters["paymentReceipts/pagination"] || {
      currentPage: 1,
      totalPages: 1,
      perPage: 200,
      total: 0,
    }
);

const handlePageChange = (page) => {
  fetchReceipts(page, pagination.value.perPage);
};
const handlePerPageChange = (perPage) => {
  fetchReceipts(1, perPage);
};

onMounted(() => {
  loadAcademicYears();
  fetchGuardians();
  fetchReceipts();
});

const openDeleteDialog = (receipt) => {
  if (receipt.id === null) {
    toast.warning(t("receipts.delete_not_allowed"));
    return;
  }
  if (receipt.is_charge_only) {
    toast.warning(t("receipts.delete_not_allowed"));
    return;
  }
  selectedReceipt.value = receipt;
  deleteDialog.value.show();
};
const printReceiptsReport = () => {
  const list = receiptsWithYearBalance.value || [];

  if (!list.length) {
    toast.info(t("receipts.empty"));
    return;
  }

  const printWindow = window.open("", "_blank");

  const css = `
    @page { size: A4; margin: 20mm; }
    body { font-family: Arial, sans-serif; line-height: 1.6; padding: 20px; }
    .header { text-align: center; margin-bottom: 20px; }
    .receipt-info { margin-bottom: 20px; }
    .details { border: 1px solid #ddd; padding: 15px; border-radius: 5px; }
    .row { display: flex; justify-content: space-between; margin-bottom: 10px; }
    .label { font-weight: bold; }
    .amount { font-size: 18px; font-weight: bold; text-align: center; margin: 20px 0; }
    .signatures { display: flex; justify-content: space-between; margin-top: 50px; }
    .signature { width: 200px; border-top: 1px solid #333; text-align: center; padding-top: 5px; }

    /* each receipt on a separate page */
    .receipt-page { page-break-after: always; }
    .receipt-page:last-child { page-break-after: auto; }
  `;

  const receiptsHtml = list
    .map((receipt) => {
      const isDebit = paidAmount(receipt) > 0;
      const amount = isDebit ? paidAmount(receipt) : receipt.credit;

      return `
        <div class="receipt-page">
          <div class="header">
            <h2>${t("receipts.single_receipt_title")}</h2>
          </div>

          <div class="receipt-info">
            <div class="row">
              <span class="label">${t("receipts.number")}:</span>
              <span>${receipt.receipt_number ?? ""}</span>
            </div>
            <div class="row">
              <span class="label">${t("receipts.academic_year")}:</span>
              <span>${receipt.academic_year?.name ?? ""}</span>
            </div>
            <div class="row">
              <span class="label">${t("receipts.date")}:</span>
              <span>${formatDate(receipt.created_at)}</span>
            </div>
          </div>

          <div class="details">
            <div class="row">
              <span class="label">${t("receipts.guardian")}:</span>
              <span>${receipt.guardian_name ?? ""}</span>
            </div>

            <div class="row">
              <span class="label">${t("receipts.student")}:</span>
              <span>${receipt.student_name ?? ""}</span>
            </div>

            <div class="row">
              <span class="label">${t("receipts.amount")}:</span>
              <span
                class="amount"
                style="color:${isDebit ? "#198754" : "#dc3545"}"
              >
                ${isDebit ? "+" : "-"} ${formatCurrency(amount)}
              </span>
            </div>

            <div class="row">
              <span class="label">${t("receipts.notes")}:</span>
              <span>${receipt.notes || ""}</span>
            </div>

            ${
              receipt.discount_type && receipt.discount_type !== "none" && receipt.discount_amount > 0
                ? `<div class="row">
                    <span class="label">${t("receipts.discount_column")}:</span>
                    <span>${t("receipts.discount_badge", { percentage: receipt.discount_percentage, amount: formatCurrency(receipt.discount_amount) })}</span>
                  </div>`
                : ""
            }
          </div>

          <div class="signatures">
            <div class="signature">${t("receipts.collector_signature")}</div>
            <div class="signature">${t("receipts.guardian_signature")}</div>
          </div>
        </div>
      `;
    })
    .join("");

  const printContent = `
    <!DOCTYPE html>
    <html dir="rtl">
      <head>
        <meta charset="UTF-8" />
        <title>${t("receipts.print_report")}</title>
        <style>${css}</style>
      </head>
      <body>
        ${receiptsHtml}
      </body>
    </html>
  `;

  printWindow.document.open();
  printWindow.document.write(printContent);
  printWindow.document.close();

  printWindow.onload = function () {
    setTimeout(() => printWindow.print(), 300);
  };
};

const deleteReceipt = async () => {
  try {
    if (!selectedReceipt.value?.id) {
      toast.error(t("receipts.no_receipt_selected"));
      return;
    }

    const resp = await api.delete(
      `/payment-receipts/${selectedReceipt.value.id}`
    );

    const status = resp?.status ?? resp?.data?.status ?? null;

    if (typeof status === "number" && status >= 400) {
      if (status === 403) {
        toast.error(t("auth.forbidden", "Forbidden"));
      } else {
        toast.error(resp?.data?.message || t("receipts.delete_failed"));
      }
      return;
    }

    if (!status || [200, 204].includes(status)) {
      toast.success(resp?.data?.message || t("receipts.delete_success"));
      await fetchReceipts(
        pagination.value.currentPage,
        pagination.value.perPage
      );
    } else {
      toast.error(t("receipts.delete_failed"));
    }
  } catch (error) {
    if (error?.response?.status === 403) {
      toast.error(t("auth.forbidden", "Forbidden"));
    } else {
      toast.error(extractApiMessage(error, "receipts.delete_error"));
    }
  } finally {
    selectedReceipt.value = null;
    deleteDialog.value.hide();
  }
};

onUnmounted(() => {
  if (debounceTimeout) clearTimeout(debounceTimeout);
});

function extractApiMessage(error, fallbackKey = "errors.generic") {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === "object" && data.message !== null) {
      const loc = (data.locale || locale.value || "en")
        .toLowerCase()
        .startsWith("ar")
        ? "ar"
        : "en";
      if (typeof data.message[loc] === "string") return data.message[loc];
    }
    if (typeof data.message === "string") return data.message;
  }
  if (error?.response?.statusText) return error.response.statusText;
  return t(fallbackKey);
}

function notifyError(error, fallbackKey = "errors.generic") {
  const s = error?.response?.status;
  if (s === 401) return toast.error(t("auth.unauthorized", "Unauthorized"));
  if (s === 403) return toast.error(t("auth.forbidden", "Forbidden"));
  return toast.error(extractApiMessage(error, fallbackKey));
}

const fetchGuardians = async () => {
  try {
    const response = await api.get("/guardians");
    guardians.value = response.data.data;
  } catch (error) {
    notifyError(error, "receipts.guardians_failed");
  }
};

const fetchReceipts = async (page = 1, perPage = pagination.value.perPage) => {
  isLoading.value = true;
  try {
    const params = {
      page,
      per_page: perPage,
      receipt_number: receiptNumberFilter.value || undefined,
      guardian_id: guardianFilter.value || undefined,
      with_deleted: true,
      with_reversals: true,
      order_by: "created_at",
      order_direction: "asc",
      academic_year_id: academicYearFilter.value || undefined,
    };
    const response = await api.get("/payment-receipts", { params });
    if (response.data && response.data.data) {
      receipts.value = response.data.data;
      if (response.data.meta) {
        store.commit("payments/SET_PAGINATION", {
          currentPage: response.data.meta.current_page,
          totalPages: response.data.meta.last_page,
          perPage: response.data.meta.per_page,
          total: response.data.meta.total,
        });
      }
    } else {
      receipts.value = [];
    }
  } catch (error) {
    notifyError(error, "receipts.loading_failed");
    receipts.value = [];
  } finally {
    isLoading.value = false;
  }
};

const debounce = (fn, delay) => {
  return (...args) => {
    if (debounceTimeout) clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => {
      fn(...args);
      debounceTimeout = null;
    }, delay);
  };
};
const debouncedSearch = debounce(() => fetchReceipts(1), 500);

const resetFilters = () => {
  receiptNumberFilter.value = "";
  guardianFilter.value = "";
  fetchReceipts(1);
};

const printSingleReceipt = (receipt) => {
  const printWindow = window.open("", "_blank");
  const isDebit = paidAmount(receipt) > 0;
  const amount = isDebit ? paidAmount(receipt) : receipt.credit;

  const printContent = `
    <!DOCTYPE html>
    <html dir="rtl">
    <head>
      <meta charset="UTF-8">
      <title>${t("receipts.single_receipt_print_title", {
        num: receipt.receipt_number,
      })}</title>
      <style>
        @page { size: A4; margin: 20mm; }
        body { font-family: Arial, sans-serif; line-height: 1.6; padding: 20px; }
        .header { text-align: center; margin-bottom: 20px; }
        .receipt-info { margin-bottom: 20px; }
        .details { border: 1px solid #ddd; padding: 15px; border-radius: 5px; }
        .row { display: flex; justify-content: space-between; margin-bottom: 10px; }
        .label { font-weight: bold; }
        .amount {
          font-size: 18px; font-weight: bold; text-align: center; margin: 20px 0;
          color: ${isDebit ? "#198754" : "#dc3545"};
        }
        .signatures { display: flex; justify-content: space-between; margin-top: 50px; }
        .signature { width: 200px; border-top: 1px solid #333; text-align: center; padding-top: 5px; }
      </style>
    </head>
    <body>
    <div class="header"><h2>${t('receipts.single_receipt_title')}</h2></div>
      <div class="receipt-info">
        <div class="row"><span class="label">${t('receipts.number')}:</span><span>${receipt.receipt_number}</span></div>
        <div class="row"><span class="label">${t('receipts.academic_year')}:</span><span>${receipt.academic_year?.name || ''}</span></div>
        <div class="row"><span class="label">${t('receipts.date')}:</span><span>${formatDate(receipt.created_at)}</span></div>
      </div>
      </div>
      <div class="details">
        <div class="row"><span class="label">${t(
          "receipts.guardian"
        )}:</span><span>${receipt.guardian_name}</span></div>
        <div class="row"><span class="label">${t(
          "receipts.student"
        )}:</span><span>${receipt.student_name}</span></div>
        <div class="row"><span class="label">${t(
          "receipts.amount"
        )}:</span><span class="amount">${isDebit ? "+" : "-"} ${formatCurrency(
    amount
  )}</span></div>
        ${
          receipt.discount_type && receipt.discount_type !== "none"
            ? `<div class="row"><span class="label">${t("receipts.discount_type")}:</span><span>${t(
                "receipts.discount_badge",
                { percentage: receipt.discount_percentage, amount: formatCurrency(receipt.discount_amount) }
              )}</span></div>`
            : ""
        }
        <div class="row"><span class="label">${t(
          "receipts.notes"
        )}:</span><span>${receipt.notes || ""}</span></div>
      </div>
      <div class="signatures">
        <div class="signature">${t("receipts.collector_signature")}</div>
        <div class="signature">${t("receipts.guardian_signature")}</div>
      </div>
    </body>
    </html>
  `;
  printWindow.document.open();
  printWindow.document.write(printContent);
  printWindow.document.close();
  printWindow.onload = function () {
    setTimeout(() => printWindow.print(), 300);
  };
};

const receiptsWithYearBalance = computed(() => {
  const sorted = [...receipts.value].sort((a, b) => {
    if (a.academic_year_id !== b.academic_year_id) return a.academic_year_id - b.academic_year_id;
    const da = new Date(a.created_at).getTime();
    const db = new Date(b.created_at).getTime();
    if (da !== db) return da - db;
    return (a.id || 0) - (b.id || 0);
  });

  const out = [];
  const yearStudentState = new Map();

  for (const r of sorted) {
    const key = `${r.academic_year_id}_${r.student_id}`;
    
    if (!yearStudentState.has(key)) {
      yearStudentState.set(key, 0);
    }

    const currentBalance = yearStudentState.get(key);
    
   const debitAmount = parseFloat(r.debit) || 0;
    const creditAmount = parseFloat(r.credit) || 0;
    // A discount forgives part of the fee without cash changing hands, so it
    // must reduce the running balance the same way a debit does — otherwise
    // a discounted receipt still leaves a phantom amount outstanding here.
    const discountAmount = parseFloat(r.discount_amount) || 0;
    const netChange = debitAmount + discountAmount - creditAmount;
    const newBalance = currentBalance + netChange;
    
    yearStudentState.set(key, newBalance);

    out.push({
      ...r,
      year_running_balance: newBalance,
      debit_display: debitAmount,
      credit_display: creditAmount
    });
  }

  return out;
});


// A discount forgives part of the fee without cash changing hands, but the
// admin still needs it to read as "paid" wherever a real payment would show
// — so the الله/"paid" column and printed amount combine debit (real cash)
// with discount_amount (forgiven fee), matching how the running balance
// already treats the two the same way.
const paidAmount = (receipt) => {
  const debit = parseFloat(receipt?.debit) || 0;
  const discount = parseFloat(receipt?.discount_amount) || 0;
  return debit + discount;
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "0.00 ";
  const numericAmount =
    typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(numericAmount)) return "0.00 ";
  
  const formatted = numericAmount.toFixed(2);
  return formatted + " ";
};


function formatDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString(locale.value || "ar", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
</script>

<style scoped>
.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
.card.bg-light {
  opacity: 0.77;
}
.opacity-75 {
  opacity: 0.75;
}
.border-warning {
  border: 1px solid #ffc107 !important;
}
</style>